// dd-modules.jsx — bespoke block-5 modules + Vasana Type blocks (V2). Ported from
// the Claude Design source `dd-modules.jsx`; `window.*` globals replaced with ES
// imports. Weights, patterns, subscales, safety shares and the cross-assessment
// indices are directional design content (not stored in the DB); the Vasana `mix`
// that drives OrgKeyDimensions and the type table IS live.
import React, { useState } from "react";
import ReactDOM from "react-dom";
import { TYPE_COLOR } from "./data";
import {
  ddScoreColor,
  DD_TYPE_INDEX, DD_INDEX_KEYS, DD_QUESTIONS,
} from "./dd-data";

// ===== Wellness: weighted contribution =====
export function WellnessWeighted({ weights }) {
  const contribs = weights.map((w) => ({ ...w, contrib: w.w * w.score }));
  const max = Math.max(...contribs.map((c) => c.contrib));
  const sorted = [...contribs].sort((a, b) => b.contrib - a.contrib);
  return (
    <div className="dd-module">
      <div className="dd-module__head">
        <h4 className="dd-module__title">Weighted contribution to the composite</h4>
        <p className="dd-module__sub">Each domain’s weight × its score. <b>Sleep</b> and <b>Energy</b> carry the most weight (0.18 each), so they move the overall number most — and both score low.</p>
      </div>
      <div className="dd-weight">
        {sorted.map((c) => (
          <div className="dd-weight__row" key={c.label}>
            <span className="dd-weight__lbl">{c.label}</span>
            <span className="dd-weight__wt">w {c.w.toFixed(2)}</span>
            <div className="dd-weight__track">
              <div className="dd-weight__fill" style={{ width: (c.contrib / max * 100) + "%", background: ddScoreColor(c.score, "A") }}></div>
            </div>
            <span className="dd-weight__val">{c.contrib.toFixed(1)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== VBI: pattern prevalence (two ranked lists) + definitions modal =====
const VBI_PRESSURE_DEFS = [
  ["Overload Burnout", "Demands are consistently exceeding recovery capacity. Energy is depleting faster than it can be restored."],
  ["Performance Erosion", "High exhaustion and low accomplishment are reinforcing each other, creating a compounding cycle of declining output and confidence."],
  ["Constraint Burnout", "High demands paired with low autonomy. People are absorbing pressure without meaningful control over how they respond to it."],
  ["Isolation Burnout", "Low connection and low belonging are removing the social buffer that protects against stress."],
  ["Emotional Withdrawal", "High disconnection and low purpose are combining to pull people back from both their work and the people in it."],
  ["Meaning Misalignment", "Low purpose and low accomplishment are converging. Work feels both ineffective and directionless."],
  ["Recovery Deficit", "Insufficient recovery alongside elevated exhaustion. Burnout is accumulating beneath the surface before it becomes visible."],
];
const VBI_PROTECTION_DEFS = [
  ["Sustainable Performance", "Effort and recovery are well matched. People can perform consistently without depleting their energy reserves."],
  ["Engaged Connection", "Strong belonging and low disconnection are building the resilience needed to absorb and manage stress."],
  ["High Impact Performance", "Strong accomplishment and high purpose are reinforcing each other, driving sustained motivation and meaningful output."],
  ["Empowered Execution", "High autonomy and strong accomplishment are aligned. People have both the freedom and the capacity to perform well."],
  ["Aligned Work Experience", "High purpose and high autonomy are coexisting. Work feels meaningful and within people's control."],
  ["Resilient System", "Strong recovery and high belonging are present together. People are resourced personally and socially to stay stable under pressure."],
];

export function VbiPatterns({ pressure, protection }) {
  const [modal, setModal] = useState(false);
  const Rank = ({ rows, tone }) => {
    const max = Math.max(...rows.map((r) => r.pct));
    return (
      <div className="dd-rank">
        {rows.map((r, i) => (
          <div className="dd-rank__row" key={r.label}>
            <span className="dd-rank__n">{String(i + 1).padStart(2, "0")}</span>
            <span className="dd-rank__lbl">{r.label}</span>
            <div className="dd-rank__track">
              <div className="dd-rank__fill" style={{ width: (r.pct / max * 100) + "%", background: tone }}></div>
            </div>
            <span className="dd-rank__pct">{r.pct}%</span>
          </div>
        ))}
      </div>
    );
  };
  return (
    <div className="dd-module">
      <div className="dd-module__head">
        <h4 className="dd-module__title">Pattern prevalence
          <button type="button" className="dd-module__info" aria-label="About pattern prevalence"
            onClick={() => setModal(true)}>
            <i className="ph ph-info"></i>
          </button>
        </h4>
        <p className="dd-module__sub">Share of the population showing each burnout pressure point and each protective anchor — a behavioral read an average score can’t give.</p>
      </div>
      <div className="dd-twocol">
        <div>
          <h5 className="dd-rank__cap dd-rank__cap--risk"><i className="ph ph-warning-octagon"></i>Pressure points</h5>
          <Rank rows={pressure} tone="#FF5C57" />
        </div>
        <div>
          <h5 className="dd-rank__cap dd-rank__cap--good"><i className="ph ph-shield-check"></i>Protection anchors</h5>
          <Rank rows={protection} tone="#4AE07A" />
        </div>
      </div>

      {ReactDOM.createPortal(
        <>
          <div className={"es-drawer-scrim" + (modal ? " is-open" : "")} onClick={() => setModal(false)}></div>
          <aside className={"es-drawer" + (modal ? " is-open" : "")} aria-hidden={!modal} role="dialog" aria-label="Pattern definitions">
            <div className="es-drawer__head">
              <div>
                <span className="es-eyebrow">Burnout · pattern prevalence</span>
                <h3 className="es-drawer__name">Pattern definitions</h3>
              </div>
              <button type="button" className="es-drawer__close" aria-label="Close" onClick={() => setModal(false)}>
                <i className="ph ph-x"></i>
              </button>
            </div>
            <div className="es-drawer__body">
              <div className="dd-pmodal__sec">
                <h4 className="dd-pmodal__label dd-pmodal__label--risk"><i className="ph ph-warning-octagon"></i>Pressure points</h4>
                <dl className="dd-pmodal__list">
                  {VBI_PRESSURE_DEFS.map(([term, def]) => (
                    <div className="dd-pmodal__item" key={term}>
                      <dt>{term}</dt><dd>{def}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="dd-pmodal__sec">
                <h4 className="dd-pmodal__label dd-pmodal__label--good"><i className="ph ph-shield-check"></i>Protective anchors</h4>
                <dl className="dd-pmodal__list">
                  {VBI_PROTECTION_DEFS.map(([term, def]) => (
                    <div className="dd-pmodal__item" key={term}>
                      <dt>{term}</dt><dd>{def}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </aside>
        </>,
        document.body
      )}
    </div>
  );
}

// ===== ProQOL: three-subscale profile =====
export function ProqolProfile({ subscales }) {
  return (
    <div className="dd-module">
      <div className="dd-module__head">
        <h4 className="dd-module__title">Three-subscale profile</h4>
        <p className="dd-module__sub">A mixed-orientation instrument, shown health-aligned. <b>Compassion Satisfaction</b> is the strength; the <b>Burnout</b> subscale is the soft spot.</p>
      </div>
      <div className="dd-profile">
        {subscales.map((s) => (
          <div className="dd-profile__card" key={s.label}>
            <div className="dd-profile__val" style={{ color: s.color || ddScoreColor(s.score, "B") }}>{s.score}</div>
            <div className="dd-profile__lbl">{s.label}</div>
            <span className={"dd-profile__tag dd-profile__tag--" + s.note.replace(/\s/g, "")}>{s.note}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Clinical safety flag callout (PHQ-9, AUDIT, PCL-5) =====
export function SafetyCallout({ safety, name }) {
  return (
    <div className="dd-safety">
      <i className="ph-fill ph-first-aid-kit"></i>
      <div>
        <h4 className="dd-safety__title">Clinical safety pathway</h4>
        <p className="dd-safety__body">
          Approximately <b>{safety.share}%</b> of respondents triggered a safety flag on {name} (via {safety.item}). Flagged responses route to a human <b>{safety.route}</b> outside this dashboard. <b>Individuals are never identified here</b> — this is an aggregate count, and clinical follow-up happens through the protected escalation channel.
        </p>
      </div>
    </div>
  );
}

// ===== Vasana: organizational key dimensions (weighted from the live type mix) =====
export function OrgKeyDimensions({ mix }) {
  const pct = Object.fromEntries(mix.map((m) => [m.type, m.pct]));
  const total = mix.reduce((s, m) => s + m.pct, 0) || 100;
  const share = (t) => ((pct[t] || 0) / total) * 100;
  const dom = [...mix].sort((a, b) => b.pct - a.pct)[0].type;
  const domColor = TYPE_COLOR[dom];

  // each dimension: score = weighted % toward the RIGHT endpoint
  const DIMS = [
    { title: "Trust Lens", left: "Sincerity", right: "Competence",
      score: share("SA") + share("MA"),
      readRight: ["Your organization leads with competence.", "You extend trust through demonstrated capability and results."],
      readLeft: ["Your organization leads with sincerity.", "You extend trust through honesty, intent, and how people show up."] },
    { title: "Natural Movement", left: "Defense", right: "Offense",
      score: share("SA") + share("RE"),
      readRight: ["Your organization moves first.", "The instinct under pressure is to advance, push, and take initiative."],
      readLeft: ["Your organization stabilizes first.", "The instinct under pressure is to steady the ground before advancing."] },
    { title: "How You Process", left: "Think First", right: "Act First",
      score: share("RE") + share("GA"),
      readRight: ["Your organization acts to gain clarity.", "You learn by moving — action comes before full analysis."],
      readLeft: ["Your organization thinks before acting.", "You seek understanding first — analysis comes before the move."] },
  ];

  const movement = share("SA") + share("RE"); // >50 = Offense
  const process = share("RE") + share("GA"); // >50 = Act First
  const postureMove = movement >= 50 ? "Offense" : "Defense";
  const postureProc = process >= 50 ? "Act First" : "Think First";

  return (
    <div className="dd-keydim">
      {DIMS.map((d) => {
        const rightActive = d.score >= 50;
        const read = rightActive ? d.readRight : d.readLeft;
        const leanFrac = (d.score - 50) / 50;
        const dir = rightActive ? 1 : -1;
        // Max tilt is 20° (8 + min(12, …)), which swings a beam end ±L·sin(20°) ≈ 43px
        // from the pivot. The viewBox must clear that plus the end cap's radius and
        // stroke (9 + 3/2), or the raised/lowered circles get clipped.
        const deg = dir * (8 + Math.min(12, Math.abs(leanFrac) * 30));
        const th = deg * Math.PI / 180;
        const R = 9, STROKE = 3, L = 126;
        const MAX_DY = L * Math.sin((20 * Math.PI) / 180);
        const PAD = Math.ceil(MAX_DY + R + STROKE / 2); // 54
        const pivotX = 150, pivotY = PAD, VB_H = PAD * 2;
        const dx = L * Math.cos(th), dy = L * Math.sin(th);
        const lx = pivotX - dx, ly = pivotY + dy;
        const rx = pivotX + dx, ry = pivotY - dy;
        const fulcrumBase = pivotY + 38;
        const gray = "rgba(255,255,255,0.32)";
        return (
          <div className="dd-keydim__card" key={d.title}>
            <h5 className="dd-keydim__title">{d.title}</h5>
            <div className="dd-seesaw">
              <svg className="dd-seesaw__svg" viewBox={`0 0 300 ${VB_H}`} role="img"
                aria-label={d.left + " versus " + d.right}>
                <polygon points={`${pivotX},${pivotY + 2} ${pivotX - 14},${fulcrumBase} ${pivotX + 14},${fulcrumBase}`} fill="#43293d" />
                <line x1={lx} y1={ly} x2={rx} y2={ry} stroke={domColor} strokeOpacity="0.6"
                  strokeWidth="3" strokeLinecap="round" />
                <circle cx={lx} cy={ly} r={R} fill={!rightActive ? domColor : gray}
                  stroke="rgba(15,7,18,0.6)" strokeWidth={STROKE} />
                <circle cx={rx} cy={ry} r={R} fill={rightActive ? domColor : gray}
                  stroke="rgba(15,7,18,0.6)" strokeWidth={STROKE} />
              </svg>
              <div className="dd-seesaw__ends">
                <span className={"dd-seesaw__end" + (!rightActive ? " is-active" : "")}
                  style={!rightActive ? { color: domColor } : null}>{d.left}</span>
                <span className={"dd-seesaw__end" + (rightActive ? " is-active" : "")}
                  style={rightActive ? { color: domColor } : null}>{d.right}</span>
              </div>
            </div>
            <p className="dd-keydim__read"><b>{read[0]}</b> {read[1]}</p>
          </div>
        );
      })}

      <div className="dd-keydim__card dd-keydim__card--posture">
        <h5 className="dd-keydim__title">50/50 Balance</h5>
        <div className="dd-keydim__pills">
          <span className="dd-keydim__pill" style={{ borderColor: domColor, color: domColor }}>{postureMove}</span>
          <span className="dd-keydim__pill" style={{ borderColor: domColor, color: domColor }}>{postureProc}</span>
        </div>
        <p className="dd-keydim__read">This combination anchors how your organization responds under pressure.</p>
      </div>
    </div>
  );
}

// ===== Vasana: cross-assessment correlation across the questions =====
export function CrossAssessment({ typeIndex = DD_TYPE_INDEX }) {
  const [open, setOpen] = useState(0);
  const idx = typeIndex || DD_TYPE_INDEX, keys = DD_INDEX_KEYS;

  // Overall pattern is derived from the live matrix (all indices are
  // health-aligned, higher = better), so it always matches the table above.
  const pick = (key, mode) =>
    Object.keys(idx).reduce((best, t) => {
      const v = idx[t] ? idx[t][key] : null;
      if (v == null) return best;
      if (!best || (mode === "max" ? v > best.val : v < best.val)) return { type: t, val: v };
      return best;
    }, null);
  const topSupport = pick("Support", "max");
  const mostBurnout = pick("Burnout", "min"); // lowest health-aligned score = most burnout
  const mostAnxiety = pick("Anxiety", "min"); // lowest score = most anxiety
  const lowSupport = pick("Support", "min");
  const nm = (x) => (x ? x.type[0] + x.type[1].toLowerCase() : "—");
  const questions = DD_QUESTIONS.map((q) => {
    // largest gap across indices between the two leaning types
    let maxKey = keys[0], maxGap = -1;
    keys.forEach((k) => {
      const g = Math.abs(idx[q.a.type][k] - idx[q.b.type][k]);
      if (g > maxGap) { maxGap = g; maxKey = k; }
    });
    return { ...q, maxKey, maxGap };
  });

  const TypeTag = ({ t }) => {
    const label = t[0] + t[1].toLowerCase();
    return <span className="dd-xa__type" style={{ color: TYPE_COLOR[t], borderColor: TYPE_COLOR[t] }}>{label}</span>;
  };

  return (
    <div className="dd-xa">
      <div className="dd-module__head">
        <h4 className="dd-module__title">Cross-assessment correlation across the questions<span className="dd-beta">Beta</span></h4>
        <p className="dd-module__sub">This view is in beta — we're still refining the underlying logic, so read it as directional. Each Vasana Type question is a forced choice between two dispositions. For the questions where those two dispositions diverge most, here is how each one scores across five key indices — the largest gap is highlighted. <b>Group tendencies, not individual predictions.</b></p>
      </div>

      <div className="dd-xa__list">
        {questions.map((q, qi) => {
          const isOpen = open === qi;
          return (
            <div key={q.qid} data-qid={q.qid} className={"dd-xa__q" + (isOpen ? " is-open" : "")}>
              <button className="dd-xa__qhead" onClick={() => setOpen(isOpen ? -1 : qi)}>
                <div className="dd-xa__opts">
                  <span className="dd-xa__opt"><TypeTag t={q.a.type} />“{q.a.text}”</span>
                  <span className="dd-xa__vs">vs</span>
                  <span className="dd-xa__opt"><TypeTag t={q.b.type} />“{q.b.text}”</span>
                </div>
                <span className="dd-xa__hint">
                  <span className="dd-xa__gapchip" style={{ borderColor: "var(--vas-on-dark-line)" }}>
                    Biggest gap · {q.maxKey} {q.maxGap}
                  </span>
                  <i className={"ph ph-caret-down dd-xa__caret"}></i>
                </span>
              </button>

              <div className="dd-xa__panel">
                <div className="dd-xa__grid">
                  <div className="dd-xa__col dd-xa__col--head">
                    <span></span>
                    <span className="dd-xa__colt" style={{ color: TYPE_COLOR[q.a.type] }}>{q.a.type[0] + q.a.type[1].toLowerCase()}</span>
                    <span className="dd-xa__colt" style={{ color: TYPE_COLOR[q.b.type] }}>{q.b.type[0] + q.b.type[1].toLowerCase()}</span>
                  </div>
                  {keys.map((k) => {
                    const va = idx[q.a.type][k], vb = idx[q.b.type][k];
                    const hot = k === q.maxKey;
                    const aWins = va >= vb;
                    return (
                      <div className={"dd-xa__row" + (hot ? " is-hot" : "")} key={k}>
                        <span className="dd-xa__k">{k}{hot ? <em> · widest</em> : null}</span>
                        <span className="dd-xa__cell">
                          <span className="dd-xa__minibar"><span style={{ width: va + "%", background: TYPE_COLOR[q.a.type], opacity: aWins ? 1 : .55 }}></span></span>
                          <b style={{ color: aWins ? "#fff" : "var(--vas-on-dark-3)" }}>{va}</b>
                        </span>
                        <span className="dd-xa__cell">
                          <span className="dd-xa__minibar"><span style={{ width: vb + "%", background: TYPE_COLOR[q.b.type], opacity: !aWins ? 1 : .55 }}></span></span>
                          <b style={{ color: !aWins ? "#fff" : "var(--vas-on-dark-3)" }}>{vb}</b>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dd-xa__pattern">
        <span className="es-intel__badge"><i className="ph ph-link"></i>Overall pattern</span>
        <ul className="es-patterns" style={{ marginTop: 12 }}>
          {topSupport && (
            <li><b style={{ color: TYPE_COLOR[topSupport.type] }}>{nm(topSupport)}</b>-leaning answers track with the strongest support ({topSupport.val}).</li>
          )}
          {mostBurnout && (
            <li><b style={{ color: TYPE_COLOR[mostBurnout.type] }}>{nm(mostBurnout)}</b>-leaning answers carry the most burnout (lowest burnout score, {mostBurnout.val}).</li>
          )}
          {(mostAnxiety || lowSupport) && (
            <li>
              {mostAnxiety && lowSupport && mostAnxiety.type === lowSupport.type ? (
                <><b style={{ color: TYPE_COLOR[mostAnxiety.type] }}>{nm(mostAnxiety)}</b>-leaning answers carry the most anxiety and the least support.</>
              ) : (
                <>
                  {mostAnxiety && <><b style={{ color: TYPE_COLOR[mostAnxiety.type] }}>{nm(mostAnxiety)}</b>-leaning answers carry the most anxiety</>}
                  {mostAnxiety && lowSupport ? "; " : ""}
                  {lowSupport && <><b style={{ color: TYPE_COLOR[lowSupport.type] }}>{nm(lowSupport)}</b>-leaning the least support</>}.
                </>
              )}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
